import React, { useState, useEffect } from 'react';
import SecuritiesBasic from './SecuritiesBasic';
import { get_securities_investors } from '../../services/common';
import moment from 'moment';

const SecuritiesInvestors = ({ fumId }) => {
  const [investors, setInvestors] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (fumId) {
      getSecuritiesInvestors();
    }
  }, [fumId]);

  const getSecuritiesInvestors = async () => {
    try {
      // const localData = JSON.parse(localStorage.getItem('caprock-local') || '{}'); // Fixed: Changed variable name
      const userToken = localStorage.getItem('systemToken'); // Fixed: Updated reference
      
      const response = await get_securities_investors(userToken, fumId);
      
      if (response.success === 200) {
        const investorsData = response.investors || [];
        const couponsData = response.coupons || [];
        
        const processedInvestors = investorsData.map(investor => ({
          ...investor,
          investor_request_date: moment(investor.investor_request_date).format('DD MMM, YYYY'),
          investor_inv_amount: typeof investor.investor_inv_amount !== 'undefined' 
            ? investor.investor_inv_amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
            : '0.00',
          principle_paid: typeof investor.principle_paid !== 'undefined' 
            ? investor.principle_paid.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
            : '0.00',
          myinvestment_maturity_date: investor.myinvestment_maturity_date 
            ? moment(investor.myinvestment_maturity_date).format('DD MMM, YYYY')
            : ''
        }));

        const processedCoupons = couponsData.map(coupon => ({
          ...coupon,
          coupon_date: coupon.coupon_date 
            ? moment(coupon.coupon_date).format('DD MMM, YYYY')
            : '',
          total_coupon_amount: typeof coupon.total_coupon_amount !== 'undefined' 
            ? coupon.total_coupon_amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
            : '0.00',
          paid_coupon_amount: typeof coupon.paid_coupon_amount !== 'undefined' 
            ? coupon.paid_coupon_amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
            : '0.00',
          balance_coupon_amount: typeof coupon.balance_coupon_amount !== 'undefined' 
            ? coupon.balance_coupon_amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
            : '0.00'
        }));

        setInvestors(processedInvestors);
        setCoupons(processedCoupons);
      } else {
        setInvestors([]);
        setCoupons([]);
      }
    } catch (error) {
      console.error('Error fetching securities investors:', error);
      setInvestors([]);
      setCoupons([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="w-full px-1 py-3 border-b">
        <div className="flex justify-center items-center py-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
          <span className="ml-2">Loading...</span>
        </div>
      </div>
    );
  }

  if (!fumId) {
    return null;
  }

  return (
    <div className="w-full px-1 py-3 border-b">
      <SecuritiesBasic fumId={fumId} />

      <div className="w-full mt-4 px-3">
        {/* Investors Table */}
        <div className="w-full overflow-x-auto mb-6">
          <h5 className="text-gray-600 text-center font-bold mb-4">Investors</h5>
          <table className="min-w-full table-auto border-collapse border border-gray-300">
            <thead>
              <tr className="bg-gray-50">
                <th className="border border-gray-300 px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Investor</th>
                <th className="border border-gray-300 px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date Invested</th>
                <th className="border border-gray-300 px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Investment Amount</th>
                <th className="border border-gray-300 px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Principle Paid</th>
                <th className="border border-gray-300 px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Maturity Date</th>
              </tr>
            </thead>
            <tbody>
              {investors.length > 0 ? (
                investors.map((investor) => (
                  <tr key={investor.investor_id} className="hover:bg-gray-50">
                    <td className="border border-gray-300 px-2 py-1 text-sm whitespace-nowrap">
                      {typeof investor.user_type !== 'undefined' && investor.user_type === 1 
                        ? `${investor.user_first_name} ${investor.user_last_name}`
                        : investor.user_company_name}
                    </td>
                    <td className="border border-gray-300 px-2 py-1 text-sm" style={{ whiteSpace: 'normal' }}>
                      {investor.investor_request_date}
                    </td>
                    <td className="border border-gray-300 px-2 py-1 text-sm whitespace-nowrap">
                      {typeof investor.investor_inv_amount !== 'undefined' 
                        ? `${investor.currency_symbol} ${investor.investor_inv_amount}`
                        : ''}
                    </td>
                    <td className="border border-gray-300 px-2 py-1 text-sm whitespace-nowrap">
                      {typeof investor.principle_paid !== 'undefined' 
                        ? `${investor.currency_symbol} ${investor.principle_paid}`
                        : ''}
                    </td>
                    <td className="border border-gray-300 px-2 py-1 text-sm whitespace-nowrap">
                      {typeof investor.myinvestment_maturity_date !== 'undefined' 
                        ? investor.myinvestment_maturity_date
                        : ''}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="border border-gray-300 px-2 py-1 text-center text-gray-500">
                    No records found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Coupons Table */}
        <div className="w-full overflow-x-auto">
          <h5 className="text-gray-600 text-center font-bold mb-4">Coupons</h5>
          <table className="min-w-full table-auto border-collapse border border-gray-300">
            <thead>
              <tr className="bg-gray-50">
                <th className="border border-gray-300 px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                <th className="border border-gray-300 px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date Due</th>
                <th className="border border-gray-300 px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Coupon Amount Due</th>
                <th className="border border-gray-300 px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Paid</th>
                <th className="border border-gray-300 px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Balance</th>
              </tr>
            </thead>
            <tbody>
              {coupons.length > 0 ? (
                coupons.map((coupon, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="border border-gray-300 px-2 py-1 text-sm whitespace-nowrap">
                      {typeof coupon.investor_name !== 'undefined' 
                        ? `Coupons Due For - ${coupon.investor_name}`
                        : ''}
                    </td>
                    <td className="border border-gray-300 px-2 py-1 text-sm" style={{ whiteSpace: 'normal' }}>
                      {typeof coupon.coupon_date !== 'undefined' 
                        ? coupon.coupon_date
                        : ''}
                    </td>
                    <td className="border border-gray-300 px-2 py-1 text-sm whitespace-nowrap">
                      {typeof coupon.total_coupon_amount !== 'undefined' 
                        ? `${coupon.currency_symbol} ${coupon.total_coupon_amount}`
                        : ''}
                    </td>
                    <td className="border border-gray-300 px-2 py-1 text-sm whitespace-nowrap">
                      {typeof coupon.paid_coupon_amount !== 'undefined' 
                        ? `${coupon.currency_symbol} ${coupon.paid_coupon_amount}`
                        : ''}
                    </td>
                    <td className="border border-gray-300 px-2 py-1 text-sm whitespace-nowrap">
                      {typeof coupon.pending_coupon_amount !== 'undefined' 
                        ? `${coupon.currency_symbol} ${coupon.pending_coupon_amount}`
                        : ''}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="border border-gray-300 px-2 py-1 text-center text-gray-500">
                    No records found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SecuritiesInvestors;
